import { tv, type VariantProps } from "@ui/lib/tv";
import { type BaseButtonProps, Button } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import { Children, createContext, type ReactNode, useContext } from "react";

/**
 * Themed content-section card ("Sparks & Influences", "What You Can Do Here",
 * "Meet the Team", …): a tinted rounded container with a double inset ring, an
 * optional full-bleed hero on top, an icon/title/description header, a free-form
 * body, and an optional centered "show all" affordance.
 *
 * The tint is a `theme` variant so sub-components inherit it via context —
 * compose them under the root.
 *
 * `ItemGrid` sizes itself to what it's given: it counts its `Item`s and picks a
 * track count that divides them, so the last row is never left part-empty. One
 * item runs full width with its icon beside the text; two split the row; four
 * land as 2×2 instead of 3+1.
 *
 * ```tsx
 * const [showAllOpen, setShowAllOpen] = useState(false);
 *
 * <SectionCard theme="green">
 *   <SectionCard.Hero>…</SectionCard.Hero>
 *   <SectionCard.Content>
 *     <SectionCard.Header icon={…} title={…} description={…} />
 *     <SectionCard.ItemGrid>
 *       <SectionCard.Item icon={…} title={…} description={…} />
 *     </SectionCard.ItemGrid>
 *     <SectionCard.ShowAll onPress={() => setShowAllOpen(true)}>…</SectionCard.ShowAll>
 *     <Dialog.Root isOpen={showAllOpen} onOpenChange={setShowAllOpen}>
 *       <Dialog.Content>…</Dialog.Content>
 *     </Dialog.Root>
 *   </SectionCard.Content>
 * </SectionCard>
 * ```
 */
export const sectionCard = tv({
  defaultVariants: { columns: "3", theme: "green" },
  slots: {
    root: "relative flex w-full flex-col overflow-clip rounded-3xl",
    // The double inset ring lives on an overlay above the children — full-bleed
    // content (Hero media, maps) would otherwise paint over an inset box-shadow
    // set on the root itself.
    ring: "pointer-events-none absolute inset-0 z-10 rounded-[inherit]",
    hero: "h-35 w-full shrink-0 xl:h-70",
    content: "flex w-full flex-col gap-4 p-4 xl:gap-10 xl:p-10",
    header: "flex flex-col gap-1 xl:gap-2",
    headerTitleRow: "flex items-center gap-2 xl:gap-3",
    headerIcon: "flex shrink-0 items-center justify-center",
    headerTitle: "font-extrabold text-2xl tracking-2xl xl:text-display-sm xl:tracking-display-sm",
    headerDescription: "text-sm xl:text-xl",
    // Track count comes from the `columns` variant — ItemGrid derives it from how
    // many items it was handed.
    itemGrid: "grid w-full gap-5 xl:gap-7",
    item: "flex flex-col items-start gap-3",
    itemIcon: "flex shrink-0 items-center justify-center",
    itemText: "flex w-full flex-col gap-1",
    itemTitle: "font-extrabold text-xl xl:text-2xl xl:tracking-2xl",
    itemDescription: "text-sm xl:text-base",
    showAll: [
      "w-full cursor-pointer rounded-sm text-center font-medium text-sm underline xl:text-base",
      "outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    ],
  },
  variants: {
    // Tracks are picked so the last row is never orphaned — four items read as
    // 2×2 rather than 3+1, two fill the row rather than stranding a third of it.
    // A lone item drops the grid and runs its icon beside the text, the shape
    // the show-all modal rows already use.
    columns: {
      "1": { item: "flex-row gap-4", itemGrid: "grid-cols-1", itemText: "w-auto min-w-0 flex-1" },
      "2": { itemGrid: "grid-cols-2" },
      "3": { itemGrid: "grid-cols-2 xl:grid-cols-3" },
    },
    theme: {
      blue: {
        root: "bg-blue-50",
        ring: "shadow-[inset_0_-1px_0_0_var(--color-blue-400),inset_0_0_0_1px_var(--color-blue-300)]",
        headerTitle: "text-blue-700",
        headerDescription: "text-blue-600",
        itemTitle: "text-blue-700",
        itemDescription: "text-blue-600",
        showAll: "text-blue-700 focus-visible:ring-blue-500",
      },
      green: {
        root: "bg-green-50",
        ring: "shadow-[inset_0_-1px_0_0_var(--color-green-200),inset_0_0_0_1px_var(--color-green-100)]",
        headerTitle: "text-green-900",
        headerDescription: "text-green-800",
        itemTitle: "text-green-900",
        itemDescription: "text-green-800",
        showAll: "text-green-900 focus-visible:ring-green-500",
      },
      purple: {
        root: "bg-purple-50",
        ring: "shadow-[inset_0_-1px_0_0_var(--color-purple-300),inset_0_0_0_1px_var(--color-purple-200)]",
        headerTitle: "text-purple-800",
        headerDescription: "text-purple-700",
        itemTitle: "text-purple-800",
        itemDescription: "text-purple-700",
        showAll: "text-purple-800 focus-visible:ring-purple-500",
      },
    },
  },
});

export type SectionCardTheme = NonNullable<VariantProps<typeof sectionCard>["theme"]>;
export type SectionCardColumns = NonNullable<VariantProps<typeof sectionCard>["columns"]>;

const SectionCardThemeContext = createContext<SectionCardTheme>("green");
/** Provided by ItemGrid so its Items can match the track count it settled on. */
const SectionCardColumnsContext = createContext<SectionCardColumns>("3");

export interface SectionCardProps {
  children: ReactNode;
  theme?: SectionCardTheme;
  className?: string;
}

function SectionCardRoot({ children, theme = "green", className }: SectionCardProps) {
  const styles = sectionCard({ theme });
  return (
    <SectionCardThemeContext.Provider value={theme}>
      <section className={styles.root({ className })}>
        {children}
        <div aria-hidden className={styles.ring()} />
      </section>
    </SectionCardThemeContext.Provider>
  );
}

function useStyles(columns?: SectionCardColumns) {
  const contextColumns = useContext(SectionCardColumnsContext);
  return sectionCard({ columns: columns ?? contextColumns, theme: useContext(SectionCardThemeContext) });
}

export interface SectionCardHeroProps {
  /** Full-bleed media (e.g. an `<Image>` sized `size-full object-cover`). */
  children: ReactNode;
  className?: string;
}

function SectionCardHero({ children, className }: SectionCardHeroProps) {
  return <div className={useStyles().hero({ className })}>{children}</div>;
}

export interface SectionCardContentProps {
  children: ReactNode;
  className?: string;
}

function SectionCardContent({ children, className }: SectionCardContentProps) {
  return <div className={useStyles().content({ className })}>{children}</div>;
}

export interface SectionCardHeaderProps {
  /** Sized by the caller (typically `size-8 xl:size-10`). Decorative — hidden from AT. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
}

function SectionCardHeader({ icon, title, description, className }: SectionCardHeaderProps) {
  const styles = useStyles();
  return (
    <header className={styles.header({ className })}>
      <div className={styles.headerTitleRow()}>
        {icon && (
          <span aria-hidden className={styles.headerIcon()}>
            {icon}
          </span>
        )}
        <h2 className={styles.headerTitle()}>{title}</h2>
      </div>
      {description && <p className={styles.headerDescription()}>{description}</p>}
    </header>
  );
}

/** Tracks that leave no orphan in the last row — four items read as 2×2, not 3+1. */
export function columnsForCount(count: number): SectionCardColumns {
  if (count === 1) return "1";
  if (count === 2 || count === 4) return "2";
  return "3";
}

export interface SectionCardItemGridProps {
  /** `Item`s. Counted to pick the track count, so pass them as a list rather than
   *  wrapped in a fragment — a fragment counts as one item. */
  children: ReactNode;
  className?: string;
}

function SectionCardItemGrid({ children, className }: SectionCardItemGridProps) {
  // `toArray` over `Children.count`: it drops nullish/boolean children, so a
  // conditional `{cond && <Item/>}` doesn't inflate the count when it's false.
  const columns = columnsForCount(Children.toArray(children).length);
  const styles = useStyles(columns);
  return (
    <SectionCardColumnsContext.Provider value={columns}>
      <ul className={styles.itemGrid({ className })}>{children}</ul>
    </SectionCardColumnsContext.Provider>
  );
}

export interface SectionCardItemProps {
  /** Sized by the caller (typically `size-8 xl:size-10`). Decorative — hidden from AT. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
}

function SectionCardItem({ icon, title, description, className }: SectionCardItemProps) {
  const styles = useStyles();
  return (
    <li className={styles.item({ className })}>
      {icon && (
        <span aria-hidden className={styles.itemIcon()}>
          {icon}
        </span>
      )}
      <div className={styles.itemText()}>
        <h3 className={styles.itemTitle()}>{title}</h3>
        {description && <p className={styles.itemDescription()}>{description}</p>}
      </div>
    </li>
  );
}

export interface SectionCardShowAllProps extends BaseButtonProps {}

function SectionCardShowAll({ className, ...props }: SectionCardShowAllProps) {
  const styles = useStyles();
  return (
    <Button
      {...props}
      className={composeRenderProps(className, (resolvedClassName) => styles.showAll({ className: resolvedClassName }))}
    />
  );
}

export const SectionCard = Object.assign(SectionCardRoot, {
  Hero: SectionCardHero,
  Content: SectionCardContent,
  Header: SectionCardHeader,
  ItemGrid: SectionCardItemGrid,
  Item: SectionCardItem,
  ShowAll: SectionCardShowAll,
});
